/* eslint-disable prefer-template */
import { ArrowForwardIosRounded } from '@mui/icons-material';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { CustomerOverview } from '../../types/customers';
import {
  isAppointmentCompleted,
  isAppointmentInPast,
  isAppointmentPending,
  isAppointmentUpcoming,
} from '../../helpers/appointments';
import Badge from '../global/Badge';
import { ROUTES } from '../../constants/global';

interface Props {
  appointment: CustomerOverview['appointments'][number];
  onSelectAppointment?: () => void;
}

function CustomerAppointmentRow({ appointment, onSelectAppointment }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    appointmentId,
    service,
    employees: employee,
    status,
    checkoutSessionCompleted,
    wasNoShow,
    appointmentType,
  } = appointment;

  const isConsultation = appointmentType === 'CONSULTATION';

  const isUpcoming =
    isAppointmentUpcoming(appointment) && !isAppointmentCompleted(appointment);
  const isPast =
    isAppointmentInPast(appointment) || isAppointmentCompleted(appointment);
  const isPending = isAppointmentPending(appointment);

  const employeeName = employee?.displayName || employee?.user.firstName || '-';
  const title = isPending
    ? `Request for ${employeeName}`
    : `${
        isConsultation ? 'Consultation' : service?.serviceName || 'Tattoo'
      } with ${employeeName}`;

  const dateToUse =
    (isUpcoming || isPast) && appointment.startDateTime
      ? format(new Date(appointment.startDateTime), 'EEEE, MMMM d') +
        ' at ' +
        format(new Date(appointment.startDateTime), 'h:mma')
      : isPending
      ? `Updated on ${format(
          new Date(appointment.updatedAt),
          'MM/dd/yyyy',
        )} at ${format(new Date(appointment.updatedAt), 'h:mma')}`
      : null;

  const badgeProps: Pick<
    React.ComponentProps<typeof Badge>,
    'colorScheme' | 'text' | 'icon'
  > | null =
    isUpcoming && status === 'CONFIRMED'
      ? {
          colorScheme: 'secondary',
          text: 'Confirmed',
          icon: CheckIcon,
        }
      : isPending && status === 'ACCEPTED'
      ? {
          colorScheme: 'secondary',
          text: 'Accepted',
          icon: CheckIcon,
        }
      : isUpcoming && status === 'SCHEDULED'
      ? {
          colorScheme: 'warning',
          text: 'Scheduled',
          icon: AccessTimeIcon,
        }
      : isPending && status === 'REQUESTED'
      ? {
          colorScheme: 'warning',
          text: 'Requested',
          icon: CheckIcon,
        }
      : isPast && checkoutSessionCompleted
      ? {
          colorScheme: 'secondary',
          text: 'Completed',
          icon: CheckIcon,
        }
      : status === 'CANCELED'
      ? {
          colorScheme: 'primary',
          text: 'Canceled',
          icon: CloseIcon,
        }
      : wasNoShow
      ? {
          colorScheme: 'primary',
          text: 'No Show',
          icon: CloseIcon,
        }
      : null;

  const to =
    isUpcoming || isPast
      ? `${ROUTES.calendar}/${appointmentId}`
      : isPending
      ? `${ROUTES.requests}/${appointmentId}`
      : '';

  const onLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>,
  ) => {
    if (!to) return;
    e.preventDefault();
    onSelectAppointment?.();
    // run on next tick to ensure the modal is closed
    setTimeout(() => {
      navigate(to);
    }, 0);
  };

  const Container = to ? Link : Box;

  return (
    <Container
      to={to}
      onClick={onLinkClick}
      style={{
        textDecoration: 'none',
      }}>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        gap={0.25}
        py={2}
        borderBottom={`1px solid ${theme.palette.lightGrey.main}`}>
        <Stack gap={0.25}>
          <Typography variant="h4" component="p">
            {title}
          </Typography>
          <Typography variant="body2" component="p">
            {dateToUse}
          </Typography>
          {badgeProps && (
            <Badge
              {...badgeProps}
              sx={{
                width: 'fit-content',
              }}
            />
          )}
        </Stack>
        {!!to && (
          <Box
            minHeight="100%"
            display="flex"
            alignItems="center"
            justifyContent="end">
            <ArrowForwardIosRounded
              sx={{
                fontSize: 18,
                color: 'text.disabled',
              }}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default CustomerAppointmentRow;
